/**
 * Shared validation primitives — Zod fragments + regex constants used
 * by every form in the app. Built off the canonical spec so changing
 * a rule here propagates to Sign-up, Members, Profile, Tenant Create,
 * Support tickets, etc. in lock-step.
 *
 * Frontend-only — the API mirrors the same shapes via
 * `flowdesk-api/src/modules/.../*.dto.js`. Keep the messages aligned
 * with what the backend returns so a 400 from the API and a
 * client-side fail surface the same wording.
 */
import { z } from 'zod'

// ─── Reusable regexes ─────────────────────────────────────────────

/**
 * A person's name: letters plus the punctuation real names actually
 * contain — apostrophes (O'Brien), hyphens (Anne-Marie) and periods
 * (J. Smith) — separated by single spaces.
 *
 * Each word must START with a letter, so `-John` / `'Brien` are still
 * rejected, as are digits and every other symbol. No leading, trailing
 * or double spaces.
 *
 * `\p{L}` (with the `u` flag) rather than `A-Za-z`: the previous rule
 * was ASCII-only, which rejected not just O'Brien but every accented or
 * non-Latin name — José, Müller, Ñoño, 李明 — in a product sold to
 * international tenants. `\p{M}` admits combining accent marks so
 * decomposed forms (e.g. "e" + U+0301) validate the same as precomposed
 * ones. The curly apostrophe `’` is accepted because iOS and Word
 * silently substitute it for `'`.
 */
export const PERSON_NAME = /^\p{L}[\p{L}\p{M}'’.-]*(?: \p{L}[\p{L}\p{M}'’.-]*)*$/u

/**
 * Organisational names (Department, Work Role) must contain at least one
 * letter somewhere.
 *
 * Digits and punctuation stay allowed — real names use them ("Level 3
 * Support", "R&D", "QA-2") — but a value with NO letters at all carries
 * no meaning, and these fields previously validated length only, so
 * "35345435" and "@#$%^&*" were accepted. `\p{L}` rather than `A-Za-z`
 * so 人事部 and Área Comercial are equally valid.
 *
 * Mirrors `CONTAINS_LETTER` in flowdesk-api/src/utils/entity-name.js —
 * keep the two identical.
 */
export const CONTAINS_LETTER = /\p{L}/u

/**
 * Emoji / pictographic symbols — rejected in names that should read as plain
 * text (task names). `\p{Extended_Pictographic}` covers the emoji pictograph
 * set; the trailing range adds the regional-indicator letters that build flag
 * emoji. Mirrors `CONTAINS_EMOJI` in flowdesk-api/src/utils/entity-name.js —
 * keep the two identical or the client accepts what the server rejects.
 */
export const CONTAINS_EMOJI = /[\p{Extended_Pictographic}\u{1F1E6}-\u{1F1FF}]/u

/** Letters + digits + common legal-name punctuation (& ' , . - / ( )),
 *  as single-spaced words (no leading/trailing or double spaces). Used for
 *  Organisation / Company Name so real names like "Acme 2025",
 *  "People's Trust", "Johnson & Johnson", "Wal-Mart" and "Smith, Jones & Co."
 *  all parse. `CONTAINS_LETTER` still requires at least one real letter, so a
 *  punctuation-only value ("& . -") is rejected.
 *  MUST stay identical to the server copies in auth.dto.js + tenants.dto.js. */
export const LETTERS_DIGITS_WITH_SINGLE_SPACES =
  /^[A-Za-z0-9&'.,()/-]+(?: [A-Za-z0-9&'.,()/-]+)*$/

/** RFC-pragmatic email regex (forbids spaces, leading/trailing dots
 *  on each label, etc.). Pair with the explicit `noSpaces`,
 *  `lowercase`, and `noConsecutiveDots` refinements below for the
 *  full "strict" treatment. */
export const EMAIL_REGEX =
  /^[a-zA-Z0-9](?:[a-zA-Z0-9._%+-]*[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/i

/** Same as `EMAIL_REGEX` but tightens the TLD to a fixed set we
 *  trust on the registration form. Helps cut down spam sign-ups
 *  using throwaway top-level domains. */
export const EMAIL_REGEX_STRICT_TLD =
  /^[a-zA-Z0-9](?:[a-zA-Z0-9._%+-]*[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.(?:com|org|net|edu|in)$/i

/** Strong password — 8-64 chars, must include uppercase, lowercase, digit,
 *  AND one non-alphanumeric symbol. The body is restricted to printable ASCII
 *  (space–`~`) so passphrases and password-manager output (hyphen, underscore,
 *  space, …) pass while emoji / exotic Unicode are rejected, which keeps bcrypt
 *  input predictable. */
export const PASSWORD_STRONG = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])[\x20-\x7E]{8,64}$/

// ─── Sanitisers ───────────────────────────────────────────────────

/** Trim ends + collapse internal runs of whitespace into a single
 *  space. Use as the `setValueAs` for react-hook-form `register`
 *  so the stored value is already clean when validation runs. */
export const trimAndCollapseSpaces = (v) =>
  typeof v === 'string' ? v.trim().replace(/\s+/g, ' ') : v

/** Lowercases an email-shaped string. Backend stores lowercase; we
 *  normalise on the client so two users can't shadow each other
 *  with "ALICE@ex.com" vs "alice@ex.com" before sign-in. */
export const lowercaseString = (v) => (typeof v === 'string' ? v.toLowerCase() : v)

// ─── Name fields ──────────────────────────────────────────────────

/** Reusable factory for `Full Name` / `Employee Name` / any other
 *  "letters + single spaces" person-name field. Pass the label so
 *  the rejection messages read naturally:
 *
 *    nameSchema('Full Name')      // → "Full Name is required."
 *    nameSchema('Employee Name')  // → "Employee Name is required."
 */
export function nameSchema(label = 'Full Name', { max = 50 } = {}) {
  return z
    .string({ message: `${label} is required.` })
    .transform(trimAndCollapseSpaces)
    .pipe(
      z
        .string()
        .min(1, `${label} is required.`)
        .min(2, `${label} must be at least 2 characters.`)
        .max(max, `${label} must not exceed ${max} characters.`)
        .regex(
          PERSON_NAME,
          `${label} can only contain letters, spaces, apostrophes, hyphens and periods.`,
        ),
    )
}

/** Company / Company Name — letters, digits, single spaces.
 *  2-50 chars. */
export function companyNameSchema(label = 'Company Name', { max = 100 } = {}) {
  return z
    .string({ message: `${label} is required.` })
    .transform(trimAndCollapseSpaces)
    .pipe(
      z
        .string()
        .min(1, `${label} is required.`)
        .min(2, `${label} must be at least 2 characters.`)
        .max(max, `${label} must be at most ${max} characters.`)
        .regex(
          LETTERS_DIGITS_WITH_SINGLE_SPACES,
          `${label} may only contain letters, numbers, spaces and & ' , . - / ( ) characters.`,
        )
        // Must read as a real name — an all-digits value like "00000" carries
        // no meaning. Mirrors the CONTAINS_LETTER rule the entity names use.
        .regex(CONTAINS_LETTER, `${label} must contain at least one letter.`),
    )
}

// ─── Email field ──────────────────────────────────────────────────

/** Email — strict variant for forms where the user is creating an
 *  account / member. Enforces:
 *    - No internal spaces
 *    - Lowercase
 *    - No consecutive dots
 *    - General email shape (and optional strict TLD whitelist)
 *    - Length cap (default 100; pass `{ max: 40 }` for the
 *      Sign-in field per the spec)
 *
 *  The `invalidMessage` lets us read "Please enter a valid Email ID."
 *  on signup vs "Invalid Email ID." on login.
 */
export function emailSchema({
  max = 100,
  strictTld = false,
  invalidMessage = 'Please enter a valid Email ID.',
} = {}) {
  return z
    .string({ message: 'Email ID is required.' })
    // Normalise rather than reject: email is case-insensitive in practice
    // (the domain always, the whole address by convention), so trim + lower-
    // case up front — dhiraj@1XL.com and dhiraj@1xl.com are the same mailbox.
    .transform((v) => (typeof v === 'string' ? v.trim().toLowerCase() : v))
    .pipe(
      z
        .string()
        .min(1, 'Email ID is required.')
        .max(max, `Email ID must not exceed ${max} characters.`)
        .refine((v) => !/\s/.test(v), 'Email ID must not contain spaces.')
        .refine((v) => !/\.\./.test(v), 'Email ID cannot contain consecutive dots.')
        .regex(strictTld ? EMAIL_REGEX_STRICT_TLD : EMAIL_REGEX, invalidMessage),
    )
}

// ─── Password field ───────────────────────────────────────────────

/** Strong password — 8-64 chars, requires the four character classes
 *  (upper / lower / digit / one non-alphanumeric symbol). All failed rules are
 *  reported in ONE composite message so the user sees every fix they
 *  need to make, not just the first Zod check that tripped. Previously
 *  a chained `.min(8).regex(...)` short-circuited: a 7-char password
 *  with special chars stopped at "at least 8 characters" and hid the
 *  composition requirement entirely (bug 0013042). The character-class
 *  restriction (printable ASCII only, no exotic Unicode) is still
 *  enforced so bcrypt input stays predictable. */
/** Factory for the strong-password field. `label` prefixes every message so a
 *  form can reuse the SAME rules with field-specific wording (e.g. the profile
 *  Change Password card wants "New Password …" instead of "Password …"). Keeps
 *  the composite behaviour: all failures reported in one message, and an
 *  invalid character (an emoji, an accented letter, any non-allowed symbol) is
 *  named explicitly rather than being mis-reported as a missing character
 *  class — the bug where "Demo@123🧑" wrongly read "must contain one uppercase
 *  …" even though it already had all four classes. */
export function makePasswordStrongSchema(label = 'Password') {
  return z
    .string({ message: `${label} is required.` })
    .superRefine((v, ctx) => {
      if (!v || v.length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${label} is required.` })
        return
      }
      const parts = []
      if (v.length < 8) parts.push(`${label} must be at least 8 characters.`)
      else if (v.length > 64) parts.push(`${label} must be at most 64 characters.`)

      const missing = []
      if (!/[a-z]/.test(v)) missing.push('one lowercase letter')
      if (!/[A-Z]/.test(v)) missing.push('one uppercase letter')
      if (!/[0-9]/.test(v)) missing.push('one number')
      if (!/[^A-Za-z0-9]/.test(v)) missing.push('one special character')
      if (missing.length > 0) {
        parts.push(`${label} must contain ${missing.join(', ')}.`)
      }
      // Reject any character outside printable ASCII (emojis, accents, exotic
      // Unicode) so bcrypt input stays predictable — and NAME emojis so the
      // user knows exactly what to remove. Only surfaced when all four required
      // classes are present so we don't stack two overlapping error sentences.
      if (missing.length === 0 && /[^\x20-\x7E]/.test(v)) {
        parts.push(
          `${label} can only use letters, numbers and standard keyboard symbols — emojis and other symbols aren’t allowed.`,
        )
      }
      if (parts.length > 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: parts.join(' ') })
      }
    })
}

export const passwordStrongSchema = makePasswordStrongSchema()

/** "Confirm your password" — wire this as a superRefine on the parent
 *  form so we can read the sibling `password` field. The empty-field
 *  message defaults to "Confirm Password is required." (the "<Field Name>
 *  is required." house pattern); pass `requiredMessage` to match a
 *  different field label, e.g. "Confirm New Password is required." on the
 *  profile Change Password card. Example:
 *
 *    schema.superRefine(confirmPasswordRefine('password', 'confirmPassword'))
 */
export function confirmPasswordRefine(
  passwordKey,
  confirmKey,
  requiredMessage = 'Confirm Password is required.',
) {
  return (data, ctx) => {
    const confirm = data?.[confirmKey]
    if (!confirm || confirm.length === 0) {
      ctx.addIssue({
        path: [confirmKey],
        code: z.ZodIssueCode.custom,
        message: requiredMessage,
      })
      return
    }
    if (confirm !== data?.[passwordKey]) {
      ctx.addIssue({
        path: [confirmKey],
        code: z.ZodIssueCode.custom,
        message: 'Passwords do not match.',
      })
    }
  }
}

// ─── Subject / Message / Description ──────────────────────────────

/** Subject from a `<select>` — the only error condition is "no
 *  option picked yet". Pair with `z.string().min(1, ...)`. */
export const subjectSelectSchema = z
  .string({ message: 'Please select a Subject.' })
  .min(1, 'Please select a Subject.')

/** Free-text Subject (support ticket / contact form). */
export function subjectTextSchema({ min = 5, max = 100 } = {}) {
  return z
    .string({ message: 'Subject is required.' })
    .transform((v) => (typeof v === 'string' ? v.trim() : v))
    .pipe(
      z
        .string()
        .min(1, 'Subject is required.')
        .min(min, `Subject must be at least ${min} characters.`)
        .max(max, `Subject must be at most ${max} characters.`)
        // Must contain a real letter AND no emojis — mirrors the backend
        // support DTO + the shared entity-name rule (letter required, emoji
        // blocked). "Login issue 🐛" is rejected for the emoji.
        .regex(CONTAINS_LETTER, 'Subject must contain at least one letter.')
        .refine((v) => !CONTAINS_EMOJI.test(v), 'Subject cannot contain emojis.'),
    )
}

/** Long-form Message (e.g. Contact Us form). Defaults to 10-500. */
export function messageSchema({ min = 10, max = 500 } = {}) {
  return z
    .string({ message: 'Message is required.' })
    .transform((v) => (typeof v === 'string' ? v.trim() : v))
    .pipe(
      z
        .string()
        .min(1, 'Message is required.')
        .min(min, `Message must be at least ${min} characters.`)
        .max(max, `Message must not exceed ${max} characters.`),
    )
}

/** Description (support ticket). Defaults to 20-1000. */
export function descriptionSchema({ min = 20, max = 1000 } = {}) {
  return z
    .string({ message: 'Description is required.' })
    .transform((v) => (typeof v === 'string' ? v.trim() : v))
    .pipe(
      z
        .string()
        .min(1, 'Description is required.')
        .min(min, `Description must be at least ${min} characters.`)
        .max(max, `Description must be at most ${max} characters.`),
    )
}

/**
 * @param {boolean} requirePassword  false for a Google-only account.
 *
 * A Google-only Identity has no `passwordHash`, so there is literally nothing
 * for the user to type and nothing for the server to compare — the backend
 * skips the proof for exactly that case. Requiring it here anyway locked those
 * accounts out of the feature entirely. The flag comes from the server
 * (`hasPassword` on the session user), never inferred client-side, so the two
 * sides cannot disagree; the backend still enforces the rule independently.
 */
export function makeEmailChangeSchema(requirePassword = true) {
  return z.object({
    newEmailId: emailSchema(),
    currentPassword: requirePassword
      ? z
          .string()
          .min(1, 'Current Password is required.')
          .max(128, 'Current Password must not exceed 128 characters.')
      : z
          .string()
          .max(128, 'Current Password must not exceed 128 characters.')
          .optional()
          .or(z.literal('')),
  })
}
